import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, Youtube, Volume2, X, Minimize2, GripHorizontal } from 'lucide-react';

const NowPlayingBar = ({ isOpen, currentSong, isStreaming, streamUrl }) => {
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('npbar_dismissed') === 'true');
  const [minimized, setMinimized] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  // Only show if there's an active song or stream, not dismissed, and DJ panel closed
  const shouldShow = isOpen && !dismissed && (currentSong || isStreaming);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('npbar_dismissed', 'true');
  };

  const handleMinimize = () => {
    setMinimized(!minimized);
  };

  // Drag handlers (mouse + touch)
  const handleDragStart = useCallback((e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStartRef.current = { x: clientX, y: clientY, posX: position.x, posY: position.y };
    setIsDragging(true);
  }, [position]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = clientX - dragStartRef.current.x;
    const dy = clientY - dragStartRef.current.y;
    setPosition({
      x: dragStartRef.current.posX + dx,
      y: dragStartRef.current.posY + dy
    });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  if (!shouldShow) return null;

  const songTitle = currentSong?.song_title || 'Live Stream';
  const artistName = currentSong?.artist_name || currentSong?.user_name || 'DJ';
  const thumbnail = currentSong?.thumbnail;

  // Minimized state - tiny floating pill
  if (minimized) {
    return (
      <div
        className="fixed z-40"
        style={{
          left: `calc(50% + ${position.x}px)`,
          bottom: `calc(80px - ${position.y}px)`,
          transform: 'translateX(-50%)',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        data-testid="now-playing-minimized"
      >
        <div
          className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/90 backdrop-blur-xl border border-fuchsia-500/30 shadow-lg cursor-pointer hover:border-fuchsia-500/60 transition-colors"
          onClick={handleMinimize}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          <Music className="w-3 h-3 text-fuchsia-400" />
          <div className="flex items-end gap-0.5 h-3">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-0.5 bg-fuchsia-400 rounded-full"
                animate={{ height: ['3px', `${5 + Math.random() * 5}px`, '3px'] }}
                transition={{ duration: 0.5 + Math.random() * 0.3, repeat: Infinity, delay: i * 0.1 }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        ref={dragRef}
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed z-40"
        style={{
          left: `calc(50% + ${position.x}px)`,
          bottom: `calc(80px - ${position.y}px)`,
          transform: 'translateX(-50%)',
          cursor: isDragging ? 'grabbing' : 'default',
          userSelect: 'none'
        }}
        data-testid="now-playing-bar"
      >
        <div className="flex items-center gap-3 pl-2 pr-1 py-1.5 rounded-full bg-black/85 backdrop-blur-xl border border-white/10 shadow-lg shadow-fuchsia-500/10 min-w-[220px] max-w-[360px]">
          {/* Drag handle */}
          <div
            className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-white/10 transition-colors flex-shrink-0 touch-none"
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            data-testid="now-playing-drag-handle"
          >
            <GripHorizontal className="w-3 h-3 text-white/30" />
          </div>

          {/* Thumbnail / Icon */}
          {thumbnail ? (
            <img src={thumbnail} alt="" className="w-7 h-7 rounded-md object-cover flex-shrink-0" />
          ) : (
            <div className="w-7 h-7 rounded-md bg-fuchsia-500/20 flex items-center justify-center flex-shrink-0">
              <Music className="w-3.5 h-3.5 text-fuchsia-400" />
            </div>
          )}

          {/* Song Info */}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-white truncate">{songTitle}</p>
            <p className="text-[10px] text-white/50 truncate flex items-center gap-1">
              <Youtube className="w-2.5 h-2.5 text-red-400 flex-shrink-0" />
              {artistName}
            </p>
          </div>

          {/* Visualizer Bars */}
          <div className="flex items-end gap-0.5 h-3.5 flex-shrink-0">
            {[0, 1, 2, 3].map((i) => (
              <motion.div
                key={i}
                className="w-0.5 bg-fuchsia-400 rounded-full"
                animate={{ height: ['3px', `${6 + Math.random() * 6}px`, '3px'] }}
                transition={{ duration: 0.5 + Math.random() * 0.3, repeat: Infinity, delay: i * 0.1 }}
              />
            ))}
          </div>

          {/* Minimize */}
          <button
            onClick={handleMinimize}
            className="p-1 rounded-full hover:bg-white/10 transition-colors flex-shrink-0"
            data-testid="now-playing-minimize-btn"
          >
            <Minimize2 className="w-3 h-3 text-white/40 hover:text-white/70" />
          </button>

          {/* Dismiss */}
          <button
            onClick={handleDismiss}
            className="p-1 rounded-full hover:bg-white/10 transition-colors flex-shrink-0"
            data-testid="now-playing-dismiss-btn"
          >
            <X className="w-3 h-3 text-white/40 hover:text-white/70" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NowPlayingBar;
